import { useEffect, useState } from 'react'
import { useDiscountRules, DiscountRule } from '@/hooks/useDiscountRules'
import { useAuth } from '@/hooks/useAuth'
import {Percent, Plus, Trash2, Save, ToggleRight, ToggleLeft, Sparkles, AlertTriangle} from 'lucide-react'

export default function DiscountRulesManager() {
  const { user } = useAuth()
  const { rules, loading, fetchRules, createRule, updateRule, deleteRule } = useDiscountRules()

  const [newMinDays, setNewMinDays] = useState('')
  const [newPercent, setNewPercent] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editPercent, setEditPercent] = useState('')

  useEffect(() => {
    fetchRules()
  }, [])

  const handleAddRule = async () => {
    const minDays = parseInt(newMinDays)
    const percent = parseFloat(newPercent)
    if (isNaN(minDays) || minDays < 1 || isNaN(percent) || percent <= 0 || percent > 100) {
      alert('Veuillez entrer des valeurs valides (jours >= 1, pourcentage entre 0 et 100)')
      return
    }

    const existing = rules.find((r) => r.minDays === minDays)
    if (existing) {
      alert(`Un rabais pour ${minDays}+ jours existe deja. Modifiez-le directement.`)
      return
    }

    setSaving(true)
    try {
      await createRule({
        minDays,
        discountPercent: percent,
        label: newLabel || `${minDays}+ jours`,
        active: true,
        creator: user?.userId || 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      setNewMinDays('')
      setNewPercent('')
      setNewLabel('')
    } catch (error) {
      alert('Erreur lors de la creation du rabais')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdatePercent = async (rule: DiscountRule) => {
    const percent = parseFloat(editPercent)
    if (isNaN(percent) || percent <= 0 || percent > 100) {
      alert('Pourcentage invalide (entre 0 et 100)')
      return
    }
    setSaving(true)
    try {
      await updateRule(rule._id!, { discountPercent: percent, updatedAt: new Date().toISOString() })
      setEditingId(null)
      setEditPercent('')
    } catch (error) {
      alert('Erreur lors de la mise a jour')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (rule: DiscountRule) => {
    try {
      await updateRule(rule._id!, { active: !rule.active, updatedAt: new Date().toISOString() })
    } catch (error) {
      alert('Erreur lors de la mise a jour')
    }
  }

  const handleDelete = async (rule: DiscountRule) => {
    if (!window.confirm(`Supprimer le rabais "${rule.label}" ?`)) return
    try {
      await deleteRule(rule._id!)
    } catch (error) {
      alert('Erreur lors de la suppression')
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-3 rounded-xl shadow-lg">
          <Percent className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-white">Rabais par duree</h2>
          <p className="text-gray-400 text-sm">Configurez les pourcentages de rabais selon le nombre de jours de location</p>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/20 rounded-xl p-4 border border-blue-700/30 mb-6">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300">
            <p className="font-semibold mb-1">Comment ca fonctionne ?</p>
            <p className="text-blue-400">Le rabais le plus avantageux applicable est automatiquement selectionne. Par exemple, pour une location de 10 jours avec un rabais de 5% a 3+ jours et 10% a 7+ jours, le rabais de 10% sera applique.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-3" />
          <div className="text-gray-400">Chargement...</div>
        </div>
      ) : (
        <>
          {/* Liste des rabais existants */}
          <div className="space-y-3 mb-8">
            {rules.length === 0 ? (
              <div className="bg-gray-800/50 rounded-xl p-8 text-center border border-gray-700">
                <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3 opacity-50" />
                <p className="text-gray-400">Aucun rabais configure. Ajoutez votre premier rabais ci-dessous.</p>
              </div>
            ) : (
              rules.map((rule) => (
                <div
                  key={rule._id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    rule.active !== false
                      ? 'bg-gradient-to-r from-gray-800 to-gray-800/80 border-green-500/30 hover:border-green-500/60'
                      : 'bg-gray-800/40 border-gray-700 opacity-60'
                  }`}
                >
                  {/* Toggle actif */}
                  <button
                    onClick={() => handleToggleActive(rule)}
                    className="flex-shrink-0 transition-all hover:scale-110"
                    title={rule.active !== false ? 'Desactiver' : 'Activer'}
                  >
                    {rule.active !== false ? (
                      <ToggleRight className="w-8 h-8 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-gray-500" />
                    )}
                  </button>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-lg">{rule.label}</span>
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">
                        a partir de {rule.minDays} jour{rule.minDays > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Pourcentage editable */}
                  <div className="flex items-center gap-2">
                    {editingId === rule._id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editPercent}
                          onChange={(e) => setEditPercent(e.target.value)}
                          className="w-20 px-3 py-2 bg-gray-700 border border-yellow-400 rounded-lg text-white text-center font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          min="0.1"
                          max="100"
                          step="0.5"
                        />
                        <span className="text-yellow-400 font-bold">%</span>
                        <button
                          onClick={() => handleUpdatePercent(rule)}
                          disabled={saving}
                          className="p-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-all hover:scale-110"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditPercent('') }}
                          className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-all"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingId(rule._id!); setEditPercent(String(rule.discountPercent)) }}
                        className="flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-2 rounded-xl font-black text-lg hover:shadow-lg hover:shadow-yellow-400/30 transition-all hover:scale-105"
                      >
                        -{rule.discountPercent}%
                      </button>
                    )}
                  </div>

                  {/* Supprimer */}
                  <button
                    onClick={() => handleDelete(rule)}
                    className="flex-shrink-0 p-2 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white rounded-lg transition-all hover:scale-110"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Formulaire d'ajout */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border-2 border-dashed border-yellow-400/30">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-yellow-400" />
              Ajouter un nouveau rabais
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-400 font-bold mb-1 uppercase">Nb jours minimum</label>
                <input
                  type="number"
                  value={newMinDays}
                  onChange={(e) => setNewMinDays(e.target.value)}
                  placeholder="Ex: 3"
                  min="1"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-bold mb-1 uppercase">Rabais (%)</label>
                <input
                  type="number"
                  value={newPercent}
                  onChange={(e) => setNewPercent(e.target.value)}
                  placeholder="Ex: 10"
                  min="0.1"
                  max="100"
                  step="0.5"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 font-bold mb-1 uppercase">Label (optionnel)</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Ex: 3+ jours"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 placeholder:text-gray-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleAddRule}
                  disabled={saving || !newMinDays || !newPercent}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl font-bold hover:shadow-2xl hover:shadow-yellow-400/50 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      Ajouter
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
